import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDateTime } from "src/utils/dateTime";
import { Box, HBox, VBox } from "@ui/box/Box";
import { Error } from "@ui/error/Error";
import Panel from "@ui/panel/Panel";
import Spacer from "@ui/spacer/Spacer";
import { TabItem, Tabs } from "@ui/tabs/Tabs";
import { Typo } from "@ui/text/Typo";
import { RecursiveCSS } from "@ui/theme/GlobalTheme";
import Signal, { s_subscribe, s_unsubscribe } from "badmfck-signal";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { GD } from "src/GD";
import { IClient } from "src/services/ClientService";
import { useRouter } from "src/services/Router";
import { ITerminal } from "src/types/types";
import { TerminalBadge } from "../conponents/TerminalBadge";
import { FormParser } from "../FormParser";
import { FormDirtyContext } from "../FormDirtyContext";
import { MerchantNavigationBar } from "./MerchantNavigationBar";
import type { MerchantDestination } from "./merchantNavigation";
import { useMerchantPopupNavigation } from "./useMerchantPopupNavigation";

// 1. у каналов экваера могут быть валюты
// 2. добавляя новую валюту к каналу - показываем всех мерчей канала и предлагаем добавить фии для этой валюты
// 3. у экваера могут быть разные терминалы и к ним комиссии
import { Badge } from "@ui/badge/Badge";
import Button from "@ui/button/Button";
import { IconAppearance } from "@ui/icons/IconAppearance";
import { IconCredentials } from "@ui/icons/IconCredentials";
import { IconFile } from "@ui/icons/IconFile";
import { IconHistory } from "@ui/icons/IconHistory";
import { IconInfo } from "@ui/icons/IconInfo";
import { IconPayments } from "@ui/icons/IconPayments";
import { IconProgress } from "@ui/icons/IconProgress";
import { IconStatus } from "@ui/icons/IconStatus";

import { checkFeature, TAvailableFeatures, TFeaturedComponent } from "src/Featuresmap";
import Decimal from "decimal.js";
import { WebsiteRequestForm } from "../conponents/popup/WebsiteRequestPopup";
import { Sessions } from "../forms/Sessions";
import { MerchantChargebacks } from "./MerchantChargebacks";
import { notify } from "src/tools/notify";
import { MerchantAdjustments, S_ADJUSTMENTS_CHANGED } from "./MerchantAdjustments";
import { ApplicationProgress } from "./ApplicationProgress";
import { MerchantEvents } from "./MerchantEvents";

import { MerchantChannels } from "../providers/MerchantChannels";
import { Card, CopyButton, DialogBody, embeddedFormSx, Facts, Mono, Muted, Notice, PopupHeader, ProviderDialog, Section, StateDot, StatusPill } from "../providers/providersUi";
import { toDate } from "../providers/providersData";
import { StatusHistory } from "./StatusHistory";
import { AdminButton } from "../pages/administration/AdminControls";
import { ArrowDownLeft, ArrowLeftRight, ArrowRight, ArrowUpRight, ChartColumn, CreditCard, Globe, Info, Percent, Send, Settings as SettingsIcon, ShieldAlert } from "lucide-react";
import styled from "styled-components";
import { StatView } from "../StatView";
import { S_TEST_PAYMENT_POPUP } from "./TestPaymentPopup";
import { useHasRole, useIsManagerRole } from "src/hooks/tools";
import { hasAnyRole, hasRole } from "src/permissions";

const parseJsonSafely = (value: unknown): unknown => {
	if (typeof value !== "string") return value ?? {};
	try {
		return JSON.parse(value);
	} catch {
		return { raw: value };
	}
};

const parseEventPayload = (value: unknown): Record<string, unknown> => {
	const parsed = parseJsonSafely(value);
	return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
};



interface TClientCredentialsPopupProps {
	onSuccess?: () => void;
	// merchant: IClientGroup;
	merchantID: number;
	clienttID?: number;
	client_user_id?: number;
	tab?: number;
	channelId?: number;
}

enum TABS {
	INFO = 1,
	SETTINGS = 3,
	TERMINALS = 4,
	APPLICATION = 5,
	MID = 6,
	PAYMENTS = 7,
	STAT = 8,
	ADJUSTMENTS = 9,
	CHARGEBACKS = 10,
}

export const S_CLIENT_CREDENTIALS_POPUP = new Signal<TClientCredentialsPopupProps | null>("S_CLIENT_CREDENTIALS_POPUP");
const s_merchant_request_update = new Signal<void>("s_merchant_request_update");

const PAYSWIX_PROJECT_ID = 3;

const TINTED_TABS: number[] = [TABS.STAT, TABS.TERMINALS, TABS.PAYMENTS, TABS.CHARGEBACKS, TABS.ADJUSTMENTS];
const TAB_SURFACE = "#f5f7fa";

const PopupNotices = styled.div`
	flex: 0 0 auto;
	padding: 12px 24px 0;
	font: 13px/1.5 "Euclid Circular B", sans-serif;
`;

/** Tabs right under the white header; the selected tab scrolls on its own. Fees sit on the tinted ground of the new style. */
const MerchantTabs = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	background: #fff;
	transition: opacity 120ms ease;
	> div:first-child {
		flex: 0 0 auto;
		overflow-x: auto;
		scrollbar-width: none;
	}
	> div:last-child {
		min-height: 0;
		padding: 20px 24px 24px;
		background: #fff;
	}
	/* Lists, tables and charts sit on the tinted ground of the new style. */
	&[data-tinted="true"] > div:last-child {
		background: #f5f7fa;
	}
	/* Payments, Chargebacks and Adjustments end with the same pager bar at the bottom edge, set off by a line. */
	&[data-section="${TABS.PAYMENTS}"] > div:last-child,
	&[data-section="${TABS.CHARGEBACKS}"] > div:last-child,
	&[data-section="${TABS.ADJUSTMENTS}"] > div:last-child {
		padding-bottom: 0;
	}
	/* Chargebacks and Adjustments scroll as a whole; their table head pins to the very top of the tab. */
	&[data-section="${TABS.CHARGEBACKS}"] > div:last-child,
	&[data-section="${TABS.ADJUSTMENTS}"] > div:last-child {
		--sticky-head-top: -24px;
	}
	[data-panel="footer"] {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 10px;
		flex-shrink: 0;
		min-height: 60px;
		padding: 12px 0;
		border-top: 1px solid #e4e7ec;
		background: ${TAB_SURFACE};
	}
	/* Info and Settings bring their own menu and pane, which run to the edges and scroll separately. */
	> div:last-child:has(.merchant-split) {
		padding: 0;
		overflow: hidden;
	}
	@media (max-width: 600px) {
		&[data-section="${TABS.CHARGEBACKS}"] > div:last-child,
		&[data-section="${TABS.ADJUSTMENTS}"] > div:last-child {
			--sticky-head-top: -16px;
		}
		> div:last-child {
			padding: 16px;
		}
	}
`;

const isPlainObject = (value: unknown): value is Record<string, any> => {
	return !!value && typeof value === "object" && !Array.isArray(value);
};

const markObjectReadonly = (data: Record<string, any>, field: string) => {
	const value = data[field];
	if (!isPlainObject(value) && !Array.isArray(value)) return;

	data[`$__${field}_disabled`] = true;
	if (Array.isArray(value)) {
		data[`$__${field}_hide_add_button`] = true;
		data[`$__${field}_ignore_delete_button`] = true;
	}

	for (const record of Array.isArray(value) ? value : [value]) {
		if (!isPlainObject(record)) continue;
		for (const key of Object.keys(record)) {
			if (!key.startsWith("$__")) record[`$__${key}_disabled`] = true;
		}
	}
};

const prepareMerchantSettingsResponse = (response: any, access: { isMerchantRole: boolean; isPayswixMerchant: boolean }) => {
	if (!access.isMerchantRole) return response;

	const settingsData = response?.data?.data;
	if (!isPlainObject(settingsData)) return response;

	markObjectReadonly(settingsData, "access");
	if (access.isPayswixMerchant) markObjectReadonly(settingsData, "payment_window");
	return response;
};

const usePayswixMerchantAccess = () => {
	useLocale();
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);

	if (!project || !user) return undefined;

	const userProject = user.projects.find(val => val.id === project.id);
	const isMerchantRole = hasRole(userProject?.roles, "merchant");

	return {
		isMerchantRole,
		isPayswixMerchant: project.id === PAYSWIX_PROJECT_ID && isMerchantRole,
	};
};

//!TODO: rename, now merchant credentials popup;
export const ClientCredentialsPopup = () => {
	useLocale();
	const router = useRouter();
	const [state, setState] = useState<"small" | "full" | "mid">("small");
	const [busy, setBusy] = useState(false);
	const request = S_CLIENT_CREDENTIALS_POPUP.use();
	const [project] = GD.B_SELECTED_PROJECT.use();
	const navigation = useMerchantPopupNavigation(request, project?.id);
	const { merchant, activeTarget, error, refresh } = navigation;
	const [selectedTab, setSelectedTab] = useState<number | undefined>(undefined);
	const [section, setSection] = useState<number>(TABS.INFO);
	const [pendingNavigation, setPendingNavigation] = useState<{ destination: MerchantDestination; direction: -1 | 1 } | null>(null);
	const dirtyForms = useRef(new Set<string>());
	const contentRef = useRef<HTMLDivElement>(null);
	const lastAnimatedMerchant = useRef<number>();
	const currentMerchantId = useRef(merchant?.id);
	currentMerchantId.current = merchant?.id;
	const reportDirty = useCallback((id: string, dirty: boolean) => {
		if (dirty) dirtyForms.current.add(id);
		else dirtyForms.current.delete(id);
	}, []);
	const setContentBusy = useCallback((value: boolean) => {
		if (currentMerchantId.current === merchant?.id) setBusy(value);
	}, [merchant?.id]);

	useEffect(() => {
		setBusy(false);
		setPendingNavigation(null);
		dirtyForms.current.clear();
		if (request) {
			if (request.tab !== undefined) {
				setSelectedTab(request.tab);
				// match popup size to the initially selected tab
				if (request.tab === 3) setState("full"); // FEES
			} else {
				setSelectedTab(undefined);
			}
		}
	}, [request, project?.id]);

	useEffect(() => {
		if (!request) return;
		const id = s_merchant_request_update.subscribe(() => { void refresh(); });
		return () => {
			s_merchant_request_update.unsubscribe(id);
		};
	}, [request, refresh]);

	useEffect(() => {
		const element = contentRef.current;
		if (!element || !merchant || busy || navigation.switching) return;
		if (!navigation.direction || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
			lastAnimatedMerchant.current = merchant.id;
			return;
		}
		if (lastAnimatedMerchant.current === merchant.id) return;
		let animation: Animation | undefined;
		// Let the new form report its loading state before starting the entrance animation.
		const frame = requestAnimationFrame(() => {
			lastAnimatedMerchant.current = merchant.id;
			animation = element.animate([
				{ opacity: 0, transform: `translateX(${navigation.direction * 22}px)` },
				{ opacity: 1, transform: "translateX(0)" },
			], { duration: 230, easing: "cubic-bezier(.2,.7,.2,1)" });
		});
		return () => { cancelAnimationFrame(frame); animation?.cancel(); };
	}, [merchant, busy, navigation.switching, navigation.direction]);

	useEffect(() => {
		if (contentRef.current) contentRef.current.inert = navigation.switching;
	}, [navigation.switching]);

	const navigate = async (destination: MerchantDestination, direction: -1 | 1) => {
		setPendingNavigation(null);
		const changesClient = merchant?.client_id !== destination.client.id;
		if (await navigation.navigate(destination, direction)) {
			dirtyForms.current.clear();
			if (changesClient) router.navigate("client", { cid: String(destination.client.id) });
		}
	};
	const requestNavigation = (destination: MerchantDestination, direction: -1 | 1) => {
		if (busy || navigation.switching) return;
		if (dirtyForms.current.size) setPendingNavigation({ destination, direction });
		else void navigate(destination, direction);
	};

	if (!request) return null;

	let sx: RecursiveCSS = {};
	if (state === "small") {
		sx = { maxWidth: "620px", minWidth: "420px", width: "99vw", minHeight: "95vh", maxHeight: "95vh" };
	} else if (state === "mid") {
		sx = { maxWidth: "960px", minWidth: "420px", width: "99vw", minHeight: "95vh", maxHeight: "95vh" };
	} else {
		sx = { maxWidth: "1200px", minWidth: "420px", width: "99vw", minHeight: "95vh", maxHeight: "95vh" };
	}

	sx = {
		...sx,
		padding: "0",
		borderRadius: "12px",
		overflow: "hidden",
		backgroundColor: "#fff",
		transition: "all .2s",
	};
	const client = navigation.neighbors?.client;
	const position = navigation.neighbors && navigation.neighbors.total > 1 ? msg("templateOf_64f93023", { v0: navigation.neighbors.position, v1: navigation.neighbors.total }) : null;
	const domain = merchant && [merchant.website_url_address, merchant.domain].find(value => value && value !== merchant.name);

	const closePopup = () => {
		setState("small");
		setSelectedTab(undefined);
		S_CLIENT_CREDENTIALS_POPUP.invoke(null);
	};

	return (
		<Panel sx={sx} busy={busy || navigation.loading} decorator="absolute" growContent disableScroll onClose={closePopup}>
			<PopupHeader
				eyebrow={
					<>{msg("website")}{client?.name ? ` · ${client.name}` : ""}
						{position && <span style={{ color: "#98a2b3" }}>· {position}</span>}
					</>
				}
				title={merchant?.name ?? msg("website")}
				actions={
					merchant && (
						<MerchantNavigationBar
							neighbors={navigation.neighbors}
							loading={navigation.neighborsLoading}
							disabled={busy || navigation.switching || !!pendingNavigation}
							error={navigation.neighborsError}
							onRetry={navigation.retryNeighbors}
							onNavigate={requestNavigation}
						/>
					)
				}
				meta={
					merchant && (
						<>
							<StateDot status={merchant.status} />
							{domain && (
								<span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
									<Globe size={13} aria-hidden="true" />
									{domain}
								</span>
							)}
							<span className="push">{msg("merchant_9ecf0256")}{merchant.id}</span>
						</>
					)
				}
				onClose={closePopup}
			/>
			{(error || pendingNavigation) && (
				<PopupNotices>
					{error && (
						<Notice
							tone="error"
							action={!merchant && <AdminButton variant="secondarySmall" size="small" onClick={() => { void refresh(); }}>{msg("retry")}</AdminButton>}>
							{error.message}
						</Notice>
					)}
					{pendingNavigation && (
						<Notice
							tone="warning"
							action={
								<div style={{ display: "flex", gap: 8 }}>
									<AdminButton variant="secondarySmall" size="small" onClick={() => setPendingNavigation(null)}>{msg("stay")}</AdminButton>
									<AdminButton variant="secondarySmall" size="small" onClick={() => { void navigate(pendingNavigation.destination, pendingNavigation.direction); }}>{msg("discardAndContinue")}</AdminButton>
								</div>
							}>{msg("thereAreUnsavedChangesStayToSaveThemOrDiscardThemAndOpen")}<strong>{pendingNavigation.destination.merchant.name}</strong>.
						</Notice>
					)}
				</PopupNotices>
			)}
			<FormDirtyContext.Provider value={reportDirty}>
			<MerchantTabs ref={contentRef} aria-busy={navigation.switching} data-tinted={TINTED_TABS.includes(section)} data-section={section} style={{ opacity: navigation.switching ? .4 : 1 }}>
			{merchant && (
				<Tabs
					spaceBottom={0}
					checkFeature={feature => checkFeature(feature as TAvailableFeatures)}
					scrollable
					variant="modern"
					selected={selectedTab}
					onSectionSelect={section => {
						setSection(section);
						if (section === TABS.APPLICATION) {
							setState("mid");
						} else if (section === TABS.SETTINGS) {
							setState("mid");
						} else if (section === TABS.TERMINALS) {
							setState("full");
						} else if (section === TABS.INFO) {
							setState("mid");
						} else if (section === TABS.PAYMENTS) {
							setState("full");
						} else if (section === TABS.CHARGEBACKS) {
							setState("full");
						} else if (section === TABS.STAT) {
							setState("mid");
						} else if (section === TABS.ADJUSTMENTS) {
							setState("full");
						} else {
							setState("mid");
						}
					}}>
					<TabItem title={msg("info_170322a3")} icon={<Info size={16} />} section={TABS.INFO}>
						<InfoSetup key={merchant.id} merchant={merchant} setBusy={setContentBusy} client_user_id={activeTarget?.client_user_id} />
					</TabItem>

					<TabItem title={msg("statistics")} icon={<ChartColumn size={16} />} section={TABS.STAT}>
						<StatView key={merchant.id} client_id={merchant.id} type="merchant" size={2} />
					</TabItem>

					{/*<TabItem title="CREDENTIALS" section={TABS.CREDENTIALS}></TabItem>*/}
					<TabItem title={msg("settings")} icon={<SettingsIcon size={16} />} section={TABS.SETTINGS}>
						<MerchantSetup setBusy={setContentBusy} merchant={merchant} />
					</TabItem>
					<TabItem title={msg("channels")} icon={<Percent size={16} />} section={TABS.TERMINALS}>
						<TerminalsTab key={merchant.id} merchant={merchant} onBusy={setContentBusy} expandChannelId={activeTarget?.channelId} />
					</TabItem>
					<TabItem title={msg("payments")} icon={<CreditCard size={16} />} section={TABS.PAYMENTS}>
						{merchant?.status !== "active" ? (
							<UnavailableBlock merchant={merchant}>{msg("paymentsAreUnavailable")}</UnavailableBlock>
						) : (
								<Sessions key={merchant.id} target_id={merchant.id} target_type="merchant" tableScrollable surface={TAB_SURFACE} />
						)}
					</TabItem>
					<TabItem title={msg("chargebacks_655ad8a5")} icon={<ShieldAlert size={16} />} section={TABS.CHARGEBACKS}>
						<MerchantChargebacks key={`chargebacks-${merchant.id}`} merchantId={merchant.id} />
					</TabItem>
					{/* <TabItem title="MID REQUEST" section={TABS.APPLICATION}>
						<ApplicationBlock merchant={merchant} setBusy={setBusy} />
					</TabItem> */}
					<TabItem title={msg("adjustments")} icon={<ArrowLeftRight size={16} />} section={TABS.ADJUSTMENTS}>
						<AdjustmentsBlock key={merchant.id} merchant={merchant} />
					</TabItem>
				</Tabs>
			)}
			</MerchantTabs>
			</FormDirtyContext.Provider>
			<PayoutPopup setBusy={setBusy} />
			<AdjustmentPopup setBusy={setBusy} />
			<ObStatusChangePopup setBusy={setBusy} />
		</Panel>
	);
};

interface IAdjustmentsBlockProps {
	merchant: IClientGroup;
}

const AdjustmentsBlock: TFeaturedComponent<IAdjustmentsBlockProps> = ({ merchant }: IAdjustmentsBlockProps) => {
	useLocale();
	const [channels] = GD.REQ_API.useRequest(undefined, {
		method: "payments/channels/merchant/list",
		data: {
			merchant_id: merchant.id,
		},
	});
	const hasOctChannel = !!channels?.data?.some(
		(channel: any) => channel?.terminal?.type === "oct" && channel?.status !== "deleted" && channel?.linked?.status !== "deleted",
	);

	if (merchant.status !== "active") {
		return <UnavailableBlock merchant={merchant}>{msg("adjustmentsAreUnavailable")}</UnavailableBlock>;
	}

	return (
		<MerchantAdjustments
			merchantId={merchant.id}
			showPayouts={hasOctChannel}
			actions={
				<>
					<AdminButton variant="secondarySmall" size="small" onClick={() => S_ADJUSTMENT_POPUP.invoke({ merchant, direction: "in" })}>
						<ArrowDownLeft size={14} />{msg("adjustmentIn_dc6ff320")}</AdminButton>
					<AdminButton variant="secondarySmall" size="small" onClick={() => S_ADJUSTMENT_POPUP.invoke({ merchant, direction: "out" })}>
						<ArrowUpRight size={14} />{msg("adjustmentOut_b0935d07")}</AdminButton>
					{hasOctChannel && (
						<AdminButton variant="primarySmall" size="small" onClick={() => S_PAYOUT_POPUP.invoke({ merchant })}>
							<Send size={14} />{msg("payout")}</AdminButton>
					)}
				</>
			}
		/>
	);
};

AdjustmentsBlock.feature = "adjustments";

interface IPayoutPopupProps {
	merchant: IClientGroup;
}

interface IAdjustmentPopupProps {
	merchant: IClientGroup;
	direction: "in" | "out";
}

interface IObStatusChangePopupProps {
	merchant: IClientGroup;
}

interface IPayoutModel {
	amount: number | string;
	currency: string;
	mid: number;
	customer: {
		name: string;
		email: string;
		countryCode: string;
		postalCode: string;
		addressLine1: string;
		addressLine2: string;
		city: string;
		birthDate: string;
	} & Record<string, any>;
	card: {
		pan: string;
	} & Record<string, any>;
}

const S_PAYOUT_POPUP = new Signal<IPayoutPopupProps | null>("S_PAYOUT_POPUP");
const S_ADJUSTMENT_POPUP = new Signal<IAdjustmentPopupProps | null>("S_ADJUSTMENT_POPUP");
const S_OB_STATUS_CHANGE_POPUP = new Signal<IObStatusChangePopupProps | null>("S_OB_STATUS_CHANGE_POPUP");

const regexValidator = (regex: RegExp, message: string, optional = false) => {
	return (value?: string | number) => {
		const str = String(value ?? "").trim();
		if (!str && optional) return null;
		if (!regex.test(str)) return message;
		return null;
	};
};

const parseMajorAmount = (value: number | string | undefined): Decimal => {
	return new Decimal(String(value ?? "").replace(/\s/g, "").replace(/,/g, ".") || "0");
};

const createPayoutModel = (merchantId: number): IPayoutModel & Record<string, any> => ({
	amount: 0,
	$__amount_type: "amount",
	$__amount_label: msg("amount"),
	$__amount_subtitle: msg("amountInMajorUnits"),
	$__amount_validator: (value?: number | string) => {
		try {
			if (parseMajorAmount(value).lte(0)) return msg("amountMustBeGreaterThan0");
			return null;
		} catch {
			return msg("invalidAmount");
		}
	},
	currency: "",
	$__currency_label: msg("currency"),
	$__currency_placeholder: "EUR",
	$__currency_subtitle: msg("currencyCode3CharsUppercase"),
	$__currency_validator: regexValidator(/^[a-zA-Z]{3}$/, msg("currencyCodeMustContain3Letters")),
	mid: merchantId,
	$__mid_type: "number",
	$__mid_label: msg("mid"),
	$__mid_validator: (value?: number) => {
		if (!Number(value) || Number(value) <= 0) return msg("midIsRequired");
		return null;
	},
	customer: {
		name: "",
		$__name_subtitle: msg("customerFullNameWillBeUsedForPaymentProcessingAndDisplayedOnCheckoutPage"),
		email: "",
		$__email_subtitle: msg("customerEmail"),
		$__email_validator: regexValidator(/^[a-zA-Z0-9_%\-+.]+@[a-zA-Z0-9-]{2,60}\.[a-zA-Z]{2,10}$/, msg("invalidCustomerEmail")),
		countryCode: "",
		$__countryCode_label: msg("countryCode"),
		$__countryCode_placeholder: "LV",
		$__countryCode_subtitle: msg("countryCodeInIsoFormat2CharsUppercase"),
		$__countryCode_validator: regexValidator(/^[a-zA-Z]{2}$/, msg("countryCodeMustContain2Letters")),
		postalCode: "",
		$__postalCode_label: msg("postalCode"),
		$__postalCode_subtitle: msg("postalCodeOfTheCustomerSAddress"),
		addressLine1: "",
		$__addressLine1_label: msg("addressLine1"),
		$__addressLine1_subtitle: msg("firstLineOfTheCustomerSAddress"),
		addressLine2: "",
		$__addressLine2_label: msg("addressLine2"),
		$__addressLine2_optional: true,
		$__addressLine2_subtitle: msg("secondLineOfTheCustomerSAddress"),
		city: "",
		$__city_subtitle: msg("cityOfTheCustomerSAddress"),
		birthDate: "",
		$__birthDate_label: msg("birthDate"),
		$__birthDate_placeholder: msg("ddmmyyyy"),
		$__birthDate_subtitle: msg("customerBirthDateInDdmmyyyyFormat"),
		$__birthDate_validator: regexValidator(/^\d{8}$/, msg("birthDateMustBeInDdmmyyyyFormat")),
	},
	$__customer_decorator: {
		title: msg("customer"),
		blocks: [
			{ blockless: true, order: ["name", "email", "countryCode", "ip"] },
			{ blockless: true, order: ["postalCode", "addressLine1", "addressLine2", "city", "birthDate"] },
		],
	},
	card: {
		pan: "",
		$__pan_label: "PAN",
		$__pan_subtitle: msg("cardPrimaryNumber"),
		$__pan_validator: regexValidator(/^[0-9\s-]{13,19}$/, msg("panMustContain1319DigitsSpacesOrHyphens")),
	},
	$__card_decorator: {
		title: msg("card"),
		blocks: [{ blockless: true, order: ["pan"] }],
	},
});

const preparePayoutPayload = (data: IPayoutModel): IPayoutModel => ({
	...data,
	amount: parseMajorAmount(data.amount).times(100).toDecimalPlaces(0).toNumber(),
	currency: data.currency.toUpperCase(),
	customer: {
		...data.customer,
		countryCode: data.customer.countryCode.toUpperCase(),
	},
	card: {
		...data.card,
		pan: data.card.pan.replace(/[\s-]/g, ""),
	},
});

const PayoutPopup = ({ setBusy }: { setBusy: (busy: boolean) => void }) => {
	useLocale();
	const request = S_PAYOUT_POPUP.use();
	if (!request) return null;

	return (
		<ProviderDialog width={760} fullHeight eyebrow={msg("templateWebsite_e4b54006", { v0: request.merchant.name })} title={msg("payout")} onClose={() => S_PAYOUT_POPUP.invoke(null)}>
			<DialogBody>
			<Notice tone="info">{msg("sendsMoneyFromTheWebsiteBalanceToACustomerSCardThroughItsOct_e77f0df2")}</Notice>
			<FormParser<IPayoutModel>
				appearance="modern"
				size="small"
				sx={embeddedFormSx}
				secondaryButton={null}
				primaryButton={{ title: msg("createPayout") }}
				onSubmit={async (data: IPayoutModel) => {
					setBusy(true);
					const response = await GD.REQ_API.request({
						method: "gate/oct",
						data: preparePayoutPayload(data),
					});
					setBusy(false);

					if (!response.error) {
						S_PAYOUT_POPUP.invoke(null);
						notify.success(msg("payoutCreated"));
						S_ADJUSTMENTS_CHANGED.invoke();
					}

					return response;
				}}
				onRequest={async () => createPayoutModel(request.merchant.id)}
			/>
			</DialogBody>
		</ProviderDialog>
	);
};

interface IAdjustmentModel {
	mid: number;
	session_id: number;
	amount: number;
}

const createAdjustmentModel = (merchantId: number): IAdjustmentModel & Record<string, any> => ({
	mid: merchantId,
	$__mid_type: "number",
	$__mid_label: msg("mid"),
	$__mid_validator: (value?: number) => {
		if (!Number(value) || Number(value) <= 0) return msg("midIsRequired");
		return null;
	},
	session_id: 0,
	$__session_id_type: "number",
	$__session_id_label: msg("sessionId"),
	$__session_id_subtitle: msg("optionalThePaymentThisCorrectionRefersTo"),
	$__session_id_optional: true,
	amount: 0,
	$__amount_type: "number",
	$__amount_optional: true,
});

const getAdjustmentMethod = (direction: IAdjustmentPopupProps["direction"]) => {
	return direction === "in" ? "gate/adjustment/in" : "gate/adjustment/out";
};

const AdjustmentPopup = ({ setBusy }: { setBusy: (busy: boolean) => void }) => {
	useLocale();
	const request = S_ADJUSTMENT_POPUP.use();
	if (!request) return null;

	const title = request.direction === "in" ? msg("adjustmentIn_dc6ff320") : msg("adjustmentOut_b0935d07");

	return (
		<ProviderDialog width={600} eyebrow={msg("templateWebsite_e4b54006", { v0: request.merchant.name })} title={title} onClose={() => S_ADJUSTMENT_POPUP.invoke(null)}>
			<DialogBody>
			<Notice tone="info">
				{request.direction === "in"
					? msg("creditsTheWebsiteBalanceLinkItToASessionWhenTheCorrectionRef_a09c5ecd")
					: msg("debitsTheWebsiteBalanceLinkItToASessionWhenTheCorrectionRefe_55ca0c80")}
			</Notice>
			<FormParser<IAdjustmentModel>
				appearance="modern"
				size="small"
				sx={embeddedFormSx}
				secondaryButton={null}
				primaryButton={{ title: title }}
				onSubmit={async (data: IAdjustmentModel) => {
					setBusy(true);
					const response = await GD.REQ_API.request({
						method: getAdjustmentMethod(request.direction),
						data,
					});
					setBusy(false);

					if (!response.error) {
						S_ADJUSTMENT_POPUP.invoke(null);
						notify.success(msg("templateCreated_4ba01aee", { v0: title }));
						S_ADJUSTMENTS_CHANGED.invoke();
					}

					return response;
				}}
				onRequest={async () => createAdjustmentModel(request.merchant.id)}
			/>
			</DialogBody>
		</ProviderDialog>
	);
};

interface IObStatusChangeModel {
	session_id: number;
	status: string;
	fail_reason: string;
	external_id: string;
}

const createObStatusChangeModel = (): IObStatusChangeModel & Record<string, any> => ({
	session_id: 0,
	$__session_id_type: "number",
	$__session_id_label: msg("sessionId"),
	$__session_id_validator: (value?: number) => {
		if (!Number(value) || Number(value) <= 0) return msg("sessionIdIsRequired");
		return null;
	},
	status: "completed",
	$__status_type: "select",
	$__status_values: ["completed", "failed", "cancelled"],
	fail_reason: "",
	$__fail_reason_label: msg("failReason"),
	$__fail_reason_optional: true,
	external_id: "",
	$__external_id_label: msg("externalId"),
	$__external_id_optional: true,
});

const ObStatusChangePopup = ({ setBusy }: { setBusy: (busy: boolean) => void }) => {
	useLocale();
	const request = S_OB_STATUS_CHANGE_POPUP.use();
	if (!request) return null;

	return (
		<ProviderDialog width={600} eyebrow={msg("templateWebsite_e4b54006", { v0: request.merchant.name })} title={msg("openBankingStatus")} onClose={() => S_OB_STATUS_CHANGE_POPUP.invoke(null)}>
			<DialogBody>
			<FormParser<IObStatusChangeModel>
				appearance="modern"
				size="small"
				sx={embeddedFormSx}
				secondaryButton={null}
				primaryButton={{ title: msg("changeStatus") }}
				onSubmit={async (data: IObStatusChangeModel) => {
					setBusy(true);
					const response = await GD.REQ_API.request({
						method: "ob/status/change",
						data,
					});
					setBusy(false);

					if (!response.error) {
						S_OB_STATUS_CHANGE_POPUP.invoke(null);
					}

					return response;
				}}
				onRequest={async () => createObStatusChangeModel()}
			/>
			</DialogBody>
		</ProviderDialog>
	);
};

declare interface ITerminalsTabProps {
	merchant: IClientGroup;
	onBusy: (busy: boolean) => void;
	expandChannelId?: number;
}

export const TerminalsTab: TFeaturedComponent<ITerminalsTabProps> = ({ merchant, expandChannelId }: ITerminalsTabProps) => {
	useLocale();
	return (
		<div>
			{merchant?.status !== "active" ? (
				<UnavailableBlock merchant={merchant}>{msg("terminalsConfigurationIsUnavailable")}</UnavailableBlock>
			) : (
				<MerchantChannels merchantId={merchant.id} merchantName={merchant.name} highlightLinkId={expandChannelId} />
			)}
		</div>
	);
};
//{/*<Fees merchantId={merchant.id} onBusy={onBusy} />*/}

TerminalsTab.feature = "clients_terminals";

export const TerminalsBlock = ({
	merchantId,
	onBusy,
	children,
	onChange,
	all,
	predefinedTerminals,
	scrollable,
	onAddTerminalClick,
}: {
	merchantId: number;
	onBusy?: (busy: boolean) => void;
	onChange: (terminal: ITerminal) => void;
	children: ReactNode;
	all?: boolean;
	predefinedTerminals?: ITerminal[] | null;
	scrollable?: boolean;
	onAddTerminalClick?: (show: boolean) => void;
}) => {
	useLocale();
	// get all terminals
	const [terminal, setTerminal] = useState<ITerminal | null>(null);
	const [terminals, setTerminals] = useState<ITerminal[] | null>(null);
	const [error, setError] = useState<IError | null>(null);

	useEffect(() => {
		loadTerminals();
	}, []);

	useEffect(() => {
		const id = s_subscribe(() => {
			loadTerminals();
		}, "merchant_terminals_refresh_" + merchantId);
		return () => {
			s_unsubscribe(id);
		};
	}, [merchantId]);

	// load terminals
	const loadTerminals = async () => {
		if (onBusy) onBusy(true);
		let data: ITerminal[] = predefinedTerminals ?? [];

		if (!predefinedTerminals && merchantId > 0) {
			const res = await GD.REQ_API.request({
				method: "merchants/terminals/list",
				data: { merchant_id: merchantId },
			});
			if (onBusy) onBusy(false);
			if (res.error) {
				setError(res.error);
				return;
			}

			data = (res.data as ITerminal[]) ?? [];
		}

		if (all && data.length > 1) {
			if (data.findIndex(t => t.id === 0) === -1)
				data.unshift({
					id: 0,
					name: msg("allTerminals"),
					link_id: -1,
					status: "active",
					type: "payment_card",
					icon: "",
					created_at: 0,
					updated_at: 0,
				});
		}

		setTerminals(data);

		if (data && data.length > 0) {
			if (!terminal) {
				//setup first terminal
				onChange(data[0]);
				setTerminal(data[0]);
			}
			if (terminal && data.findIndex(t => t.id === terminal.id) === -1) {
				//terminal was removed
				onChange(data[0]);
				setTerminal(data[0]);
			}

			if (terminal && data.findIndex(t => t.id === terminal.id) !== -1) {
				//refresh terminal
				const t = data.find(t => t.id === terminal.id);
				if (t) {
					onChange(t);
					setTerminal(t);
				}
			}
		}
	};

	if (!terminals) return null;

	let content = children;
	if (scrollable) {
		content = <Box sx={{ position: "absolute", width: "calc(100% - 40px)" }}>{children}</Box>;
	}

	return (
		<VBox
			sx={{
				position: "relative",
				flexGrow: 1,
			}}>
			<Error error={error} />

			<HBox
				sx={{
					flexGrow: 1,
				}}>
				<VBox
					sx={{
						overflowY: "auto",
						minWidth: "185px",
					}}>
					{terminals &&
						terminals.map(t => (
							<HBox
								sx={{
									borderLeft: "5px solid " + (terminal?.id === t.id ? "rgb(0, 145, 255)" : "transparent"),
									borderBottom: "1px solid rgba(0,0,0,0.1)",
									cursor: "pointer",
									padding: "20px",
									//borderRadius: "8px",
									gap: "10px",
									alignItems: "center",
									whiteSpace: "nowrap",
									overflow: "hidden",
								}}
								key={t.id}
								onClick={() => {
									if (onChange) onChange(t);
									setTerminal(t);
								}}>
								<TerminalBadge terminal={t} padding={0} size={20} simple />
								<Typo
									bold={terminal?.id === t.id}
									variant="medium"
									sx={{
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}>
									{t.name}
								</Typo>
								<Box
									sx={{
										width: "6px",
										borderRadius: "50%",
										height: "6px",
										backgroundColor: !t.status ? "green" : t.status === "active" ? "green" : "red",
									}}
								/>
							</HBox>
						))}

					{!terminals ||
						(terminals.length === 0 && (
							<Typo variant="caption" sx={{ padding: "20px" }}>{msg("noTerminalsFound")}</Typo>
						))}

					{onAddTerminalClick && (
						<>
							<Spacer grow />
							<Button variant="secondarySmall" title={msg("addTerminal")} onClick={() => onAddTerminalClick(true)} />
						</>
					)}
				</VBox>
				<Spacer size="20px" />

				<VBox
					sx={{
						flexGrow: 1,
						backgroundColor: "#ebedf461",
						padding: "20px",
						position: scrollable ? "relative" : undefined,
						overflowY: scrollable ? "auto" : undefined,
					}}>
					{content}
				</VBox>
			</HBox>
		</VBox>
	);
};

const WebsiteApplicationBlock = ({ setBusy, merchant }: { setBusy: (busy: boolean) => void; merchant: IClientGroup }) => {
	useLocale();
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);

	//let showAdminControls: boolean = false;
	let allowEditApplication: boolean = true;
	if (project && user) {
		const userProject = user.projects.find(val => val.id === project.id);
		if (userProject) {
			if (hasRole(userProject.roles, "merchant") && userProject.roles.length === 1) {
				allowEditApplication = false;
			}
		}
	}

	return (
		<WebsiteRequestForm
			appearance="modern"
			allowEdit={project && allowEditApplication}
			onComplete={() => {
				s_merchant_request_update.invoke();
			}}
			clientID={merchant.client_id}
			merchantID={merchant.id}
			setBusy={setBusy}
		/>
	);
};

const infoSectionIcons: Record<string, React.FC<IconClip>> = {
	info: IconInfo,
	application: IconFile,
	progress: IconProgress,
	events: IconHistory,
};

/** A tab split like Client settings: a white section menu, and the section itself on the tinted ground; both scroll on their own. */
const SplitLayout = ({ menu, children }: { menu: ReactNode; children: ReactNode }) => { useLocale(); return ((
	<div className="merchant-split" style={{ display: "flex", flex: "1 1 0", minHeight: 0, overflow: "hidden" }}>
		<VBox sx={{ overflowY: "auto", minHeight: 0, width: "185px", flexShrink: 0, paddingTop: "16px", borderRight: "1px solid #edf0f5", backgroundColor: "#fff" }}>{menu}</VBox>
		<VBox sx={{ flex: "1 1 0", minWidth: 0, minHeight: 0, overflow: "auto", backgroundColor: TAB_SURFACE, padding: "16px", "& > *": { flexShrink: 0 } }}>
			{children}
		</VBox>
	</div>
)); };

const InfoSetup = ({ setBusy, merchant, client_user_id }: { setBusy: (busy: boolean) => void; merchant: IClientGroup; client_user_id?: number }) => {
	useLocale();
	//!TODO: access;
	const isManagerRole = useIsManagerRole(client_user_id);
	const isSupport = useHasRole("support");

	//!TODO: crit;
	const [project] = GD.REQ_CURRENT_PROJECT.useRequest();
	const isPayswix = project && project.id === 3;

	const sections = project && isManagerRole && isPayswix ? (["info", "application", "progress", "events"] as const) : (["info"] as const);
	const [section, setSection] = useState<(typeof sections)[number]>(sections[0]);

	useEffect(() => {
		if (isSupport && !isManagerRole) setSection("info");
		else if (merchant.status !== "active") setSection("progress");
	}, [merchant, isSupport, isManagerRole]);

	useEffect(() => {
		if (!isManagerRole && section === "application") setSection("info");
	}, [isManagerRole, section]);

	let content = null;
	switch (section) {
		case "progress":
			content = <ApplicationProgress merchant={merchant} onOpenApplication={isManagerRole ? () => setSection("application") : undefined} />;
			break;
		case "info":
			content = (
				<InfoBlock
					setBusy={setBusy}
					merchant={merchant}
					onClose={() => {
						S_CLIENT_CREDENTIALS_POPUP.invoke(null);
					}}
				/>
			);
			break;
		case "application":
			content = <WebsiteApplicationBlock setBusy={setBusy} merchant={merchant} />;
			break;
		case "events":
			content = <MerchantEvents merchantId={merchant.id} />;
			break;
	}

	return (
		<SplitLayout
			menu={sections.map(sec => {
				return (
					<HBox
						key={sec}
						sx={{
							borderLeft: "5px solid " + (sec === section ? "rgb(0, 145, 255)" : "transparent"),
							borderBottom: "1px solid rgba(0,0,0,0.1)",
							cursor: "pointer",
							flexShrink: 0,
							padding: "20px",
							gap: "10px",
							alignItems: "center",
							whiteSpace: "nowrap",
							overflow: "hidden",
						}}
						onClick={() => {
							setSection(sec);
						}}>
						{(() => {
							const Icon = infoSectionIcons[sec];
							return Icon ? <Icon color={sec === section ? "rgb(0, 145, 255)" : "#666"} /> : null;
						})()}

						<Typo
							bold={sec === section}
							variant="medium"
							sx={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								textTransform: "capitalize",
							}}>
							{sec}
						</Typo>
					</HBox>
				);
			})}>
			{content}
		</SplitLayout>
	);
};

const MerchantSetup: TFeaturedComponent<{ setBusy: (busy: boolean) => void; merchant: any; client_user_id?: number }> = ({
	setBusy,
	merchant,
	client_user_id,
}) => {
	useLocale();
	//!TODO: hook?
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);

	let isMerchant = true;

	if (project && user) {
		const userProject = user.projects.find(val => val.id === project.id);
		if (userProject) {
			isMerchant = !hasAnyRole(userProject.roles, ["admin", "aml"]);
		}
	}

	//!TODO: "appearance" hidden;
	const sections = isMerchant ? ["credentials"] : (["credentials", "status"] as const);
	const [section, setSection] = useState<(typeof sections)[number]>(sections[0]);

	const merchantSectionIcons: Record<string, React.FC<IconClip>> = {
		credentials: IconCredentials,
		status: IconStatus,
		appearance: IconAppearance,
		fees: IconPayments,
	};

	let content = null;
	switch (section) {
		case "appearance":
			content = <SettingsBlock key={merchant.id} setBusy={setBusy} merchant={merchant} />;
			break;
		case "status":
			content = <StatusesBlock key={merchant.id} setBusy={setBusy} merchant={merchant} />;
			break;
		case "credentials":
			content = <ClientCredentialsBlock key={merchant.id} setBusy={setBusy} merchant={merchant} client_user_id={client_user_id} />;
			break;
		// case "fees":
		// 	content = <MerchantFees merchantId={merchant.id} onBusy={setBusy} />;
		// 	break;
	}

	return (
		<SplitLayout
			menu={sections.map(sec => {
				return (
					<HBox
						key={sec}
						sx={{
							borderLeft: "5px solid " + (sec === section ? "rgb(0, 145, 255)" : "transparent"),
							borderBottom: "1px solid rgba(0,0,0,0.1)",
							cursor: "pointer",
							flexShrink: 0,
							padding: "20px",
							gap: "10px",
							alignItems: "center",
							whiteSpace: "nowrap",
							overflow: "hidden",
						}}
						onClick={() => {
							setSection(sec);
						}}>
						{(() => {
							const Icon = merchantSectionIcons[sec];
							return Icon ? <Icon color={sec === section ? "rgb(0, 145, 255)" : "#666"} width={24} height={24} /> : null;
						})()}

						<Typo
							bold={sec === section}
							variant="medium"
							sx={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								textTransform: "capitalize",
							}}>
							{sec}
						</Typo>
					</HBox>
				);
			})}>
			{content}
		</SplitLayout>
	);
};

// const MerchantFees = ({ merchantId }: { merchantId: number; onBusy: (busy: boolean) => void }) => {
// 	return <Box>Merchant fees {merchantId}</Box>;
// };

const SettingsBlock = ({ setBusy, merchant }: { setBusy: (busy: boolean) => void; merchant: any }) => {
	useLocale();
	const payswixMerchantAccess = usePayswixMerchantAccess();

	if (merchant.status !== "active") {
		return <UnavailableBlock merchant={merchant}>{msg("appearanceConfigurationIsUnavailable")}</UnavailableBlock>;
	}

	if (!payswixMerchantAccess) {
		return (
			<VBox center>
				<Typo variant="medium">{msg("loading")}</Typo>
			</VBox>
		);
	}

	return (
		<FormParser
			appearance="modern"
			size="small"
			sx={embeddedFormSx}
			key="settings"
			buttons={[
				{
					title: msg("openMerchant"),
					variant: "secondarySmall",
					onClick: () => {
						//todo: open terminal page
					},
				},
			]}
			onSubmit={async (data: unknown) => {
				setBusy(true);
				const res = await GD.REQ_API.request({
					method: "settings/set",
					data: data,
				});
				setBusy(false);
				return prepareMerchantSettingsResponse(res, payswixMerchantAccess);
			}}
			onRequest={async () => {
				setBusy(true);
				const res = await GD.REQ_API.request({
					method: "settings/get",
					data: {
						target_id: merchant.id,
						target_type: "merchant",
						type: "merchant_settings",
					},
				});
				setBusy(false);
				return prepareMerchantSettingsResponse(res, payswixMerchantAccess);
			}}
		/>
	);
};

MerchantSetup.feature = "merchant_settings";

interface IMerchantStatusModel {
	merchant_status: {
		status: string;
		reason: string;
	} & Record<string, any>;
}
const getChangeStatusModel = (status: string): IMerchantStatusModel & Record<string, any> => {
	return {
		merchant_status: {
			status: status,
			$__status_type: "select",
			$__status_values: ["created", "submitted", "aml_review", "rejected", "board_review", "active", "blocked", "deleted"],
			reason: "",
			$__reason_type: "textarea",
		},
	};
};

export const StatusesBlock = ({ setBusy, merchant }: { setBusy: (busy: boolean) => void; merchant: any }) => {
	useLocale();
	const [history, req, _hreset, _hBusy] = GD.REQ_API.useRequest(undefined, {
		method: "events/list",
		data: {
			target_id: merchant.id,
			target_type: "merchant",
			action: "merchant_status_change",
		},
	});

	//!TODO: hook?
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);

	const userProject = project && user ? user.projects.find(val => val.id === project.id) : undefined;
	const showControls = hasAnyRole(userProject?.roles, ["admin", "aml"]);
	const historyItems = Array.isArray(history?.data) ? history.data : [];

	return (
		<VBox gap="10px">
			{showControls && (
				<>
					<Section title={msg("changeStatus")} />
					<FormParser<IMerchantStatusModel>
						primaryButton={{ title: msg("changeStatus") }}
						appearance="modern"
						size="small"
						sx={embeddedFormSx}
						secondaryButton={null}
						key="statuses"
						onSubmit={async (data: IMerchantStatusModel): Promise<any> => {
							setBusy(true);
							const response = await GD.REQ_API.request({
								method: "clients/group/status/change",
								data: {
									group_id: merchant.id,
									client_id: merchant.client_id,
									status: (data as any).merchant_status.status,
									reason: (data as any).merchant_status.reason,
								},
							});

							if (response.error) {
								setBusy(false);
								return response;
							}

							const reloadResponse = await GD.REQ_API.request({
								method: "clients/group/status/get",
								data: {
									group_id: merchant.id,
									client_id: merchant.client_id,
								},
							});

							setBusy(false);

							if (reloadResponse.error) return reloadResponse;

							req({
								method: "events/list",
								data: {
									target_id: merchant.id,
									target_type: "merchant",
									action: "merchant_status_change",
								},
							});

							s_merchant_request_update.invoke();

							return getChangeStatusModel(reloadResponse.data);
						}}
						onRequest={async (): Promise<IMerchantStatusModel> => {
							setBusy(true);
							const response = await GD.REQ_API.request({
								method: "clients/group/status/get",
								data: {
									group_id: merchant.id,
									client_id: merchant.client_id,
								},
							});
							setBusy(false);

							if (response.error) return response as any;

							return getChangeStatusModel(response.data);
						}}
					/>
				</>
			)}
			<StatusHistory aria-label={msg("statusHistory_5e75e48f")} style={showControls ? undefined : { marginTop: 0, paddingTop: 0, borderTop: 0 }}>
				<h3>{msg("statusChangeHistory")}</h3>
				<div className="entries">
					{historyItems.map((data: any, index: number) => {
						const payload = parseEventPayload(data.data);
						const oldStatus = payload.old_status ? String(payload.old_status) : "";
						const newStatus = payload.new_status ? String(payload.new_status) : "";
						return (
							<article key={data.id ?? index}>
								<div className="meta">
									<span className="author">{data.user_name ?? msg("templateUser_039247f9", { v0: data.user_id })}</span>
									<time dateTime={data.created_at}>
										<DateTimeTooltip value={data.created_at}>{formatDisplayDateTime(data.created_at, { separator: " " })}</DateTimeTooltip>
									</time>
								</div>
								<div className="transition">
									<span className="status">{oldStatus || "—"}</span>
									<ArrowRight size={16} strokeWidth={1.5} color="#98a2b3" aria-hidden="true" />
									<span className="status" data-status={newStatus}>
										{newStatus || "—"}
									</span>
								</div>
								{payload.reason ? <p>{String(payload.reason)}</p> : null}
							</article>
						);
					})}
					{historyItems.length === 0 && <div className="empty">{msg("noStatusChangesYet")}</div>}
				</div>
			</StatusHistory>
		</VBox>
	);
};

const ClientCredentialsBlock = ({ setBusy, merchant }: { setBusy: (busy: boolean) => void; merchant: any; client_user_id?: number }) => {
	useLocale();
	const payswixMerchantAccess = usePayswixMerchantAccess();
	//const isManagerRole = useIsManagerRole(client_user_id);

	if (merchant.status !== "active") {
		return <UnavailableBlock merchant={merchant}>{msg("credentialConfigurationIsUnavailable")}</UnavailableBlock>;
	}

	if (!payswixMerchantAccess) {
		return (
			<VBox center>
				<Typo variant="medium">{msg("loading")}</Typo>
			</VBox>
		);
	}

	//!TODO: manage role;
	//console.log("isManagerRole", isManagerRole);

	return (
		<>
		<Section title={msg("credentials_d48a617c")} />
		<FormParser
			key="credentials"
			appearance="modern"
			size="small"
			sx={embeddedFormSx}
			secondaryButton={{
				title: msg("testPayment"),
				onClick: (data: any) => {
					S_TEST_PAYMENT_POPUP.invoke({
						merchant: merchant,
						sec: data.data.access,
						url: data.data.payment_window.url,
						onSuccess: () => {
							s_merchant_request_update.invoke();
						},
					});
					/*GD.S_TEST_PAYMENT.invoke({
						access: data.data.access,
						url: data.data.payment_window.url,
					});*/
				},
			}}
			onSubmit={async (data: any) => {
				setBusy(true);
				const response = await GD.REQ_API.request({
					method: "settings/set",
					data: data,
				});
				setBusy(false);
				return prepareMerchantSettingsResponse(response, payswixMerchantAccess);
			}}
			onRequest={async () => {
				setBusy(true);
				const response = await GD.REQ_API.request({
					method: "settings/get",
					data: {
						target_id: merchant.id,
						target_type: "merchant",
						type: "credentials",
					},
				});
				setBusy(false);
				return prepareMerchantSettingsResponse(response, payswixMerchantAccess);
			}}
		/>
		</>
	);
};

const InfoBlock = ({ merchant, onClose }: { merchant: IClientGroup; setBusy: (busy: boolean) => void; onClose: () => void }) => {
	useLocale();
	const [client, setClient] = useState<IClient | null>(null);
	const router = useRouter();

	useEffect(() => {
		if (client && merchant.client_id !== client.id) {
			setClient(null);
		}
		const loadClient = async () => {
			const res = await GD.REQ_API.request({
				method: "clients/get",
				data: { id: merchant.client_id },
			});
			if (res.data) setClient(res.data as IClient);
		};
		loadClient();
	}, [merchant.client_id]);

	return (
		<div>
			{client && (
				<>
					<Section
						title={msg("client")}
						action={
							<AdminButton
								variant="secondarySmall"
								size="small"
								onClick={() => {
									router.navigate("client", { cid: client.id + "" });
									onClose();
								}}>{msg("openClientPage")}</AdminButton>
						}
					/>
					<Card>
						<div className="card-body">
							<Facts>
								<dt>{msg("clientName")}</dt>
								<dd>{client.name}</dd>
								<dt>{msg("clientId_8726db01")}</dt>
								<dd>
									<Mono>{client.id}</Mono>
									<CopyButton value={String(client.id)} label={msg("copyClientId")} />
								</dd>
								<dt>{msg("status")}</dt>
								<dd>{client.status ? <StatusPill status={client.status} /> : <Muted>—</Muted>}</dd>
								<dt>{msg("country")}</dt>
								<dd>{client.country || <Muted>—</Muted>}</dd>
							</Facts>
						</div>
					</Card>
				</>
			)}

			<Section title={msg("commonInfo")} />
			<Card>
				<div className="card-body">
					<Facts>
						<dt>{msg("name")}</dt>
						<dd>{merchant.name}</dd>
						<dt>{msg("merchantId_656646fc")}</dt>
						<dd>
							<Mono>{merchant.id}</Mono>
							<CopyButton value={String(merchant.id)} label={msg("copyMerchantId")} />
						</dd>
						<dt>{msg("status")}</dt>
						<dd>
							<StatusPill status={merchant.status} />
						</dd>
						<dt>{msg("createdAt")}</dt>
						<dd><DateTimeTooltip value={toDate(merchant.created_at) ? merchant.created_at : undefined}>{moment(merchant.created_at)}</DateTimeTooltip></dd>
						<dt>{msg("updatedAt")}</dt>
						<dd><DateTimeTooltip value={toDate(merchant.updated_at) ? merchant.updated_at : undefined}>{moment(merchant.updated_at)}</DateTimeTooltip></dd>
						<dt>{msg("externalMerchantId")}</dt>
						<dd>{merchant.merchant_external_id ? <Mono>{merchant.merchant_external_id}</Mono> : <Muted>—</Muted>}</dd>
					</Facts>
				</div>
			</Card>

			<Section title={msg("webInfo")} />
			<Card>
				<div className="card-body">
					<Facts>
						<dt>{msg("websiteUrl")}</dt>
						<dd>{merchant.domain || <Muted>—</Muted>}</dd>
						<dt>{msg("description")}</dt>
						<dd>{merchant.description || <Muted>—</Muted>}</dd>
					</Facts>
				</div>
			</Card>

			<Section title={msg("webAccess")} />
			<Card>
				<div className="card-body">
					<Facts>
						<dt>{msg("testUserName")}</dt>
						<dd>{merchant.test_user_name || <Muted>—</Muted>}</dd>
						<dt>{msg("testUserPassword")}</dt>
						<dd>{merchant.test_user_password ? <Mono>{merchant.test_user_password}</Mono> : <Muted>—</Muted>}</dd>
						<dt>{msg("supportPhone")}</dt>
						<dd>{merchant.support_phone || <Muted>—</Muted>}</dd>
						<dt>{msg("supportEMail")}</dt>
						<dd>{merchant.support_email || <Muted>—</Muted>}</dd>
					</Facts>
				</div>
			</Card>
		</div>
	);
};

/** A server timestamp in the configured display zone, or a dash. */
const moment = (value: unknown) => {
	const date = toDate(value);
	return date ? formatDisplayDateTime(date) : <Muted>—</Muted>;
};

declare interface IApplicationBlockProps {
	merchant: IClientGroup;
	setBusy: (busy: boolean) => void;
}

const ApplicationBlock: TFeaturedComponent<IApplicationBlockProps> = ({ merchant, setBusy }: IApplicationBlockProps) => {
	useLocale();
	const [status, setStatus] = useState<string | null>(null);

	if (merchant.status !== "active") {
		return <UnavailableBlock merchant={merchant}>{msg("applicationFormIsUnavailable")}</UnavailableBlock>;
	}
	return (
		<>
			<HBox sx={{ alignItems: "center", padding: "10px 20px", backgroundColor: "#f5f5f5", boxShadow: "0px 2px rgba(0,0,0,0.1)", borderRadius: "6px" }}>
				<Typo variant="medium">
					<b>{msg("applicationStatus")}</b>
				</Typo>
				<Spacer size="20px" />
				<Badge label={status ?? "..."} />
				<Spacer grow />
				{status === "submitted" && (
					<Button
						title={msg("requestMid")}
						variant="primarySmall"
						onClick={async () => {
							setBusy(true);
							const req = await GD.REQ_API.request({
								method: "registration/mid/request",
								data: {
									merchant_id: merchant.id,
								},
							});
							setBusy(false);
							if (!req.error && req.data && req.data.success) {
								setStatus("requested");
							}
						}}
					/>
				)}
			</HBox>
			<Spacer size="20px" />
			<FormParser
				key="application"
				scrollable
				secondaryButton={null}
				onSubmit={async (data: any) => {
					setBusy(true);
					const response = await GD.REQ_API.request({
						method: "registration/mid/set",
						data: data,
					});

					setBusy(false);
					if (!response.error) {
						s_merchant_request_update.invoke();
					}
					if (response.data && response.data.status) setStatus(response.data.status);
					return response;
				}}
				onRequest={async () => {
					setBusy(true);
					const response = await GD.REQ_API.request({
						method: "registration/mid/get",
						data: {
							id: -1,
							merchant_id: merchant.id,
						},
					});
					setBusy(false);
					setStatus(response.data.status);
					return response;
				}}
			/>
		</>
	);

	//return <WebsiteRequestForm merchantID={merchant.id} clientID={merchant.client_id} setBusy={setBusy} onComplete={onUpdate} />;
};

ApplicationBlock.feature = "mid_request";

const UnavailableBlock = ({ merchant, children }: { merchant: IClientGroup; children: React.ReactNode }) => {
	useLocale();
	if (merchant.status === "deleted" || merchant.status === "blocked" || merchant.status === "rejected") {
		return (
			<VBox center>
				<Typo
					variant="medium"
					bold
					sx={{
						backgroundColor: "rgba(255,0,0,.1)",
						padding: "20px",
						textAlign: "center",
						borderRadius: "4px",
						display: "block",
						border: "1px solid rgba(255,0,0,0.3)",
						color: "#c62828",
					}}>{msg("applicationHasBeen")}{merchant.status}.
				</Typo>
			</VBox>
		);
	}

	return (
		<VBox center>
			<Typo
				variant="medium"
				bold
				sx={{
					backgroundColor: "rgba(0,0,0,.05)",
					padding: "20px",
					textAlign: "center",
					borderRadius: "4px",
					display: "block",
					border: "1px solid rgb(0,0,0,0.1)",
				}}>{msg("merchantIn")}{merchant.status}{msg("status_1ef0b04c")}<br /> {children}
			</Typo>
		</VBox>
	);
};
